/** @format */
import { Component, DestroyRef, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StateService } from '@uirouter/core';
import { OpenUniversityCart, OpenUniversityCartUpdateRequest, OpenUniversityPaymentSystemPayment } from 'common-typescript/types';
import { catchError, EMPTY, map, Observable, Subject, throwError } from 'rxjs';
import { AuthService, LocaleService } from 'sis-common';
import {
    AppErrorHandler,
    OpenUniversityCartCustomerService,
    OpenUniversityPaymentSystemService,
    PrivatePersonEntityService,
    StudyVoucherPaymentService,
} from 'sis-components';
import { OpenUniversityErrorHelperService } from '../service/open-university-error-helper.service';
import { CART_PURCHASE_WIZARD_STEP_KEYS, CART_PURCHASE_WIZARD_STEP_KEYS_AUTO_ACTIVATE, CartPaymentType, STEP } from './constants';

@Component({
    selector: 'app-open-university-cart',
    templateUrl: './open-university-cart.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class OpenUniversityCartComponent implements OnInit {
    constructor(
        private readonly appErrorHandler: AppErrorHandler,
        private readonly localeService: LocaleService,
        private readonly openUniversityCartCustomerService: OpenUniversityCartCustomerService,
        private readonly openUniversityErrorHelper: OpenUniversityErrorHelperService,
        private readonly openUniversityPaymentSystemService: OpenUniversityPaymentSystemService,
        private readonly stateService: StateService,
        private readonly authService: AuthService,
        private readonly privatePersonService: PrivatePersonEntityService,
        private readonly studyVoucherPaymentService: StudyVoucherPaymentService,
        private readonly destroyedRef: DestroyRef,
    ) {}

    showAutoConfrimWizard: boolean;
    wizardProgressSteps: readonly string[];

    @ViewChild('currentStepTitle', { read: ElementRef }) currentStepTitle: ElementRef;
    currentStep: STEP.CHECK_SELECTIONS | STEP.CONTACT_INFORMATION = STEP.CHECK_SELECTIONS;

    cart: OpenUniversityCart;
    submitClickedSubject: Subject<void> = new Subject<void>();
    requestInProgress = false;
    checkEnrolments$ = new Subject<void>();
    loaded = false;
    paymentType: CartPaymentType = 'MONETARY_PAYMENT';

    get STEP() {
        return STEP;
    }

    /**
     * Translation key for the back button title of the current step.
     */
    get backButtonTitle(): string {
        switch (this.currentStep) {
            case STEP.CHECK_SELECTIONS:
                return 'SIS_COMPONENTS.BUTTON.EXIT';
            case STEP.CONTACT_INFORMATION:
                return 'SIS_COMPONENTS.BUTTON.PREVIOUS';
            default:
                throw new Error(`currentStep value ${this.currentStep} not supported`);
        }
    }

    /**
     * Translation key for the forward button title of the current step. On the contact information step each payment type
     * gets its own label, signalling that the user is redirected to a payment system (and to Opin.fi for study vouchers).
     */
    get forwardButtonTitle(): string {
        if (this.currentStep === STEP.CONTACT_INFORMATION) {
            switch (this.paymentType) {
                case 'STUDY_VOUCHER_PAYMENT':
                    return 'OPEN_UNIVERSITY_CART.WIZARD.CONTACT_INFORMATION.STUDY_VOUCHER_PAYMENT.CONTINUE_BUTTON';
                case 'MONETARY_PAYMENT':
                    return 'OPEN_UNIVERSITY_CART.WIZARD.CONTACT_INFORMATION.MONETARY_PAYMENT.CONTINUE_BUTTON';
                default: {
                    // If someone later adds a new value to CartPaymentType, TypeScript will complain here.
                    const unreachable: never = this.paymentType;
                    throw new Error(`Unsupported payment type: ${unreachable}`);
                }
            }
        }

        return 'SIS_COMPONENTS.BUTTON.CONTINUE';
    }

    ngOnInit(): void {
        this.openUniversityCartCustomerService
            .getCurrentCart(true)
            .pipe(this.appErrorHandler.defaultErrorHandler())
            .subscribe({
                next: cart => {
                    this.loaded = true;
                    this.cart = cart;
                },
            });
        this.initWizardConfirmTypeForUser();
    }

    back(): void {
        if (!this.loaded) {
            return;
        }

        switch (this.currentStep) {
            case STEP.CHECK_SELECTIONS:
                this.exit();
                break;
            case STEP.CONTACT_INFORMATION:
                this.currentStep = STEP.CHECK_SELECTIONS;
                this.autofocusNewStepHeading();
                break;
        }
    }

    private exit() {
        this.stateService.go('student.search.open-university');
    }

    forward(): void {
        if (!this.loaded || !this.cart) return;

        if (this.currentStep === STEP.CHECK_SELECTIONS) {
            this.checkSelectionsForward();
        } else if (this.currentStep === STEP.CONTACT_INFORMATION) {
            this.submitClickedSubject.next(); // child component (<app-cart-contact-information>) listens to this
        }
    }

    proceedToContactInformation(): void {
        this.currentStep = STEP.CONTACT_INFORMATION;
        this.autofocusNewStepHeading();
    }

    checkSelectionsForward(): void {
        this.checkEnrolments$.next();
    }

    continueToPaymentSystem(openUniversityCartUpdateRequest: OpenUniversityCartUpdateRequest): void {
        this.requestInProgress = true;
        this.initializePayment(openUniversityCartUpdateRequest)
            .pipe(
                catchError(error => this.handlePaymentInitializationError(error)),
                this.appErrorHandler.defaultErrorHandler(),
                takeUntilDestroyed(this.destroyedRef),
            )
            .subscribe()
            .add(() => (this.requestInProgress = false));
    }

    private initializePayment(openUniversityCartUpdateRequest: OpenUniversityCartUpdateRequest): Observable<void> {
        switch (this.paymentType) {
            case 'STUDY_VOUCHER_PAYMENT':
                return this.initializeStudyVoucherPayment(openUniversityCartUpdateRequest);
            case 'MONETARY_PAYMENT':
                return this.initializeMonetaryPayment(openUniversityCartUpdateRequest);
            default: {
                // If someone later adds a new value to CartPaymentType, TypeScript will complain here.
                const unreachable: never = this.paymentType;
                throw new Error(`Unsupported payment type: ${unreachable}`);
            }
        }
    }

    private initializeStudyVoucherPayment(openUniversityCartUpdateRequest: OpenUniversityCartUpdateRequest): Observable<void> {
        return this.studyVoucherPaymentService.initializeStudyVoucherPaymentAndNavigateToOpinFi(
            this.cart.id,
            this.localeService.getCurrentLanguage(),
            openUniversityCartUpdateRequest,
        );
    }

    private initializeMonetaryPayment(openUniversityCartUpdateRequest: OpenUniversityCartUpdateRequest): Observable<void> {
        return this.openUniversityPaymentSystemService
            .initializePaymentAndNavigateToPaymentSystem(
                this.cart.id,
                this.localeService.getCurrentLanguage(),
                openUniversityCartUpdateRequest,
            )
            .pipe(map<OpenUniversityPaymentSystemPayment, void>(() => undefined));
    }

    private handlePaymentInitializationError(error: any): Observable<never> {
        if (error?.status === 422) {
            return throwError(() => error);
        }
        this.openUniversityErrorHelper.showPaymentInitializationFailureAlert();
        return EMPTY;
    }

    private initWizardSteps(stepKeys: readonly string[]): void {
        this.wizardProgressSteps = stepKeys;
    }

    private initWizardConfirmTypeForUser() {
        if (this.authService.loggedIn()) {
            this.privatePersonService
                .getIsValidStudent(this.authService.personId())
                .pipe(this.appErrorHandler.defaultErrorHandler())
                .subscribe({
                    next: isValid => {
                        this.initWizardSteps(isValid ? CART_PURCHASE_WIZARD_STEP_KEYS_AUTO_ACTIVATE : CART_PURCHASE_WIZARD_STEP_KEYS);
                        this.showAutoConfrimWizard = isValid;
                    },
                });
        } else {
            this.initWizardSteps(CART_PURCHASE_WIZARD_STEP_KEYS);
            this.showAutoConfrimWizard = false;
        }
    }

    /**
     * When moving between steps _inside_ the wizard (not when entering it), autofocus the {@link CartWizardStepHeadingComponent} to help
     * screen readers start from the essential, _new_ information.
     * Autofocus must not be done when entering the wizard, because we want to give the user a chance to see the context first.
     */
    private autofocusNewStepHeading(): void {
        this.currentStepTitle?.nativeElement?.focus();
    }
}
